import type { JSONContent } from "@tiptap/react";
import { type LucideIcon } from "lucide-react";

import { EntityFragment, FrequencyEnum } from "@/__generated__/graphql";
import type { ViewMode } from "@/components/organisms/DataView";
import type {
  ThreadContextType,
  AddBankAccountContextType,
  ConnectAccountSourceContextType,
  ConnectBankAccountContextType,
  ConnectMailboxContextType,
  ConnectStripeAccountContextType,
  ReconnectBankAccountContextType,
  CreateNodeContextType,
  ReviewNodesContextType,
  ReviewUploadContextType,
  SearchContextType,
  SelectEntitiesContextType,
  SelectEntityContextType,
  SyncAccountContextType,
  ToggleColorSchemeContextType,
  TogglePanelVisibilityContextType,
  ToggleSidebarVisibilityContextType,
  ToggleStyleThemeContextType,
  UploadToFinancialAccountContextType,
  MergeBankAccountsContextType,
  ReadDocumentAgainContextType,
  SendDocumentContextType,
  SelectFinancialAccountContextType,
  UpdateNodeContextType,
  UploadFilesContextType,
} from "@/app/app/_/commands/";
import type { COMMAND_ID_PREFIX as THREAD_COMMAND_ID_PREFIX } from "@/app/app/_/commands/ThreadCommand";
import type { COMMAND_ID as CONNECT_ACCOUNT_SOURCE_COMMAND_ID } from "@/app/app/_/commands/ConnectAccountSourceCommand";
import type { COMMAND_ID as ADD_BANK_ACCOUNT_COMMAND_ID } from "@/app/app/_/commands/AddBankAccountCommand";
import type { COMMAND_ID as CONNECT_BANK_ACCOUNT_COMMAND_ID } from "@/app/app/_/commands/ConnectBankAccountCommand";
import type { COMMAND_ID as CONNECT_STRIPE_ACCOUNT_COMMAND_ID } from "@/app/app/_/commands/ConnectStripeAccountCommand";
import type { COMMAND_ID as CONNECT_MAILBOX_COMMAND_ID } from "@/app/app/_/commands/ConnectMailboxCommand";
import type { COMMAND_ID as RECONNECT_BANK_ACCOUNT_COMMAND_ID } from "@/app/app/_/commands/ReconnectBankAccountCommand";
import type { COMMAND_ID_PREFIX as CREATE_NODE_COMMAND_ID_PREFIX } from "@/app/app/_/commands/CreateNodeCommand";
import type { COMMAND_ID as REVIEW_NODES_COMMAND_ID } from "@/app/app/_/commands/ReviewNodesCommand";
import type { COMMAND_ID as REVIEW_UPLOAD_COMMAND_ID } from "@/app/app/_/commands/ReviewUploadCommand";
import type { COMMAND_ID as SEARCH_COMMAND_ID } from "@/app/app/_/commands/SearchCommand";
import type { COMMAND_ID as SELECT_ENTITIES_COMMAND_ID } from "@/app/app/_/commands/SelectEntitiesCommand";
import type { COMMAND_ID as SELECT_ENTITY_COMMAND_ID } from "@/app/app/_/commands/SelectEntityCommand";
import type { COMMAND_ID as SYNC_ACCOUNT_COMMAND_ID } from "@/app/app/_/commands/SyncAccountCommand";
import type { COMMAND_ID as TOGGLE_COLOR_SCHEME_COMMAND_ID } from "@/app/app/_/commands/ToggleColorSchemeCommand";
import type { COMMAND_ID as TOGGLE_PANEL_VISIBILITY_COMMAND_ID } from "@/app/app/_/commands/TogglePanelVisibilityCommand";
import type { COMMAND_ID as TOGGLE_SIDEBAR_VISIBILITY_COMMAND_ID } from "@/app/app/_/commands/ToggleSidebarVisibilityCommand";
import type { COMMAND_ID_PREFIX as UPDATE_NODE_COMMAND_ID_PREFIX } from "@/app/app/_/commands/UpdateNodeCommand";
import type { COMMAND_ID as UPLOAD_FILES_COMMAND_ID } from "@/app/app/_/commands/UploadFilesCommand";
import type { COMMAND_ID as UPLOAD_TO_FINANCIAL_ACCOUNT_COMMAND_ID } from "@/app/app/_/commands/UploadToFinancialAccountCommand";
import type { COMMAND_ID as SELECT_FINANCIAL_ACCOUNT_COMMAND_ID } from "@/app/app/_/commands/SelectFinancialAccountCommand";
import type { COMMAND_ID as MERGE_BANK_ACCOUNTS_COMMAND_ID } from "@/app/app/_/commands/MergeBankAccountsCommand";
import type { COMMAND_ID as READ_DOCUMENT_AGAIN_COMMAND_ID } from "@/app/app/_/commands/ReadDocumentAgainCommand";
import type { COMMAND_ID as SEND_DOCUMENT_COMMAND_ID } from "@/app/app/_/commands/SendDocumentCommand";
import {
  DialogSizeType,
  FilterDefinitionType,
  FilterType,
  SortableDefinitionsType,
  SortableType,
} from "@/components/@types";
import type { Values } from "@/constants";

/**
 * A part of a triage row the person may hide: its line of tools as a whole,
 * or on it, its tags, its documents, the other business it is booked in.
 */
export type TriageRowPart = "tools" | "tags" | "documents" | "businesses";

export type ActiveTabNodeIdType = {
  [key in keyof typeof Values.APP_VIEW_TAB_MODE_MAP as (typeof Values.APP_VIEW_TAB_MODE_MAP)[key]["type"] extends "single"
    ? key
    : never]: {
    [tab in (typeof Values.APP_VIEW_TAB_MODE_MAP)[key]["values"][number]]:
      | string
      | null;
  };
};

export type AppViewTabModeMapType = {
  [key in keyof typeof Values.APP_VIEW_TAB_MODE_MAP as (typeof Values.APP_VIEW_TAB_MODE_MAP)[key]["type"] extends "single"
    ? key
    : key]: {
    [tab in (typeof Values.APP_VIEW_TAB_MODE_MAP)[key]["values"][number]]: {
      activeNodeId?: string;
      activeNodeType?: string;
      activeTab?: string;
    };
  };
};

export type AppViewsType = keyof typeof Values.APP_VIEW_TAB_MODE_MAP;

export type AppViewTabModeType =
  | "all"
  | "archived"
  | "automations"
  | "balance-sheet"
  | "bank-feed"
  | "bills"
  | "cash-flow"
  | "completed"
  | "credit"
  | "customers"
  | "debit"
  | "deleted"
  | "editor"
  | "employees"
  | "executions"
  | "focus"
  | "general"
  | "posted"
  | "unposted"
  | "unpaid-invoices"
  | "incoming"
  | "invoices"
  | "match"
  | "outgoing"
  | "overview"
  | "pending"
  | "profit-loss"
  | "suppliers"
  | "unreconciled";

/**
 * Sentinel thread-id key for the "New thread" tab: what belongs to a thread
 * before it exists (its tab, its unsent draft) is kept under this key.
 */
export const NEW_THREAD_THREAD_ID = "__new__" as const;
export type ThreadIdType = string | typeof NEW_THREAD_THREAD_ID;

export type ThreadActionsType = {
  setActiveMessageContext: (context: ThreadMessageContextItemType[]) => void;
  setActiveMessageIndex: (index: number) => void;
  setIsSendingMessage: (isSending: boolean) => void;
  /** One-shot draft written into the thread composer (consumed on apply). */
  setComposerDraft: (draft: string | null) => void;
  /**
   * Keep what is typed in a tab's composer so it survives switching tabs;
   * ``null`` once the composer is empty.
   */
  setThreadDraft: (tabId: ThreadIdType, draft: JSONContent | null) => void;
  setIsPageContextEnabled: (isEnabled: boolean) => void;
};

export type ThreadMessageContextItemType = {
  id: string;
  identifier: string;
  name: string;
  typename: string;
  /**
   * The page's own reference, offered by ``usePageThreadReference``, not one
   * the user added: the chat shows it along the composer's foot, sends it
   * only while ``isPageContextEnabled``, and keeps it through the user's own
   * sends and new threads until the page takes it back.
   */
  isPage?: boolean;
};

export type ThreadSliceType = ThreadStateType & ThreadActionsType;

export type ThreadStateType = {
  activeMessageContext: ThreadMessageContextItemType[];
  activeMessageIndex: number;
  isSendingMessage: boolean;
  composerDraft: string | null;
  /** Unsent composer content per tab; a tab with nothing typed has no entry. */
  threadDrafts: Record<ThreadIdType, JSONContent>;
  /**
   * Whether what pages offer the chat about themselves is used: off, the
   * page's references are not sent.
   */
  isPageContextEnabled: boolean;
};

// Union of all command context types
export type CommandContextType =
  | SelectEntitiesContextType
  | SelectEntityContextType
  | SearchContextType
  | SyncAccountContextType
  | ConnectStripeAccountContextType
  | ConnectBankAccountContextType
  | ConnectMailboxContextType
  | ConnectAccountSourceContextType
  | AddBankAccountContextType
  | ReviewNodesContextType
  | ReviewUploadContextType
  | UploadFilesContextType
  | UploadToFinancialAccountContextType
  | MergeBankAccountsContextType
  | ReadDocumentAgainContextType
  | SendDocumentContextType
  | SelectFinancialAccountContextType
  | ToggleColorSchemeContextType
  | TogglePanelVisibilityContextType
  | ToggleSidebarVisibilityContextType
  | ToggleStyleThemeContextType
  | CreateNodeContextType
  | UpdateNodeContextType
  | ThreadContextType;

// disable format for this type
export type PushToCommandStackInputType =
  | {
      commandDefinitionId: typeof SELECT_ENTITIES_COMMAND_ID;
      context?: SelectEntitiesContextType;
    }
  | {
      commandDefinitionId: typeof SELECT_ENTITY_COMMAND_ID;
      context?: SelectEntityContextType;
    }
  | {
      commandDefinitionId: typeof SEARCH_COMMAND_ID;
      context?: SearchContextType;
    }
  | {
      commandDefinitionId: typeof CONNECT_STRIPE_ACCOUNT_COMMAND_ID;
      context?: ConnectStripeAccountContextType;
    }
  | {
      commandDefinitionId: typeof CONNECT_BANK_ACCOUNT_COMMAND_ID;
      context?: ConnectBankAccountContextType;
    }
  | {
      commandDefinitionId: typeof CONNECT_MAILBOX_COMMAND_ID;
      context?: ConnectMailboxContextType;
    }
  | {
      commandDefinitionId: typeof RECONNECT_BANK_ACCOUNT_COMMAND_ID;
      context?: ReconnectBankAccountContextType;
    }
  | {
      commandDefinitionId: typeof CONNECT_ACCOUNT_SOURCE_COMMAND_ID;
      context?: ConnectAccountSourceContextType;
    }
  | {
      commandDefinitionId: typeof ADD_BANK_ACCOUNT_COMMAND_ID;
      context?: AddBankAccountContextType;
    }
  | {
      commandDefinitionId: typeof SYNC_ACCOUNT_COMMAND_ID;
      context?: SyncAccountContextType;
    }
  | {
      commandDefinitionId: typeof REVIEW_NODES_COMMAND_ID;
      context?: ReviewNodesContextType;
    }
  | {
      commandDefinitionId: typeof REVIEW_UPLOAD_COMMAND_ID;
      context?: ReviewUploadContextType;
    }
  | {
      commandDefinitionId: typeof UPLOAD_FILES_COMMAND_ID;
      context?: UploadFilesContextType;
    }
  | {
      commandDefinitionId: typeof UPLOAD_TO_FINANCIAL_ACCOUNT_COMMAND_ID;
      context?: UploadToFinancialAccountContextType;
    }
  | {
      commandDefinitionId: typeof MERGE_BANK_ACCOUNTS_COMMAND_ID;
      context?: MergeBankAccountsContextType;
    }
  | {
      commandDefinitionId: typeof READ_DOCUMENT_AGAIN_COMMAND_ID;
      context?: ReadDocumentAgainContextType;
    }
  | {
      commandDefinitionId: typeof SEND_DOCUMENT_COMMAND_ID;
      context?: SendDocumentContextType;
    }
  | {
      commandDefinitionId: typeof SELECT_FINANCIAL_ACCOUNT_COMMAND_ID;
      context?: SelectFinancialAccountContextType;
    }
  | {
      commandDefinitionId: typeof TOGGLE_COLOR_SCHEME_COMMAND_ID;
      context?: ToggleColorSchemeContextType;
    }
  | {
      commandDefinitionId: typeof TOGGLE_PANEL_VISIBILITY_COMMAND_ID;
      context?: TogglePanelVisibilityContextType;
    }
  | {
      commandDefinitionId: typeof TOGGLE_SIDEBAR_VISIBILITY_COMMAND_ID;
      context?: ToggleSidebarVisibilityContextType;
    }
  | {
      commandDefinitionId: `${typeof CREATE_NODE_COMMAND_ID_PREFIX}${string}`;
      context?: CreateNodeContextType;
    }
  | {
      commandDefinitionId: `${typeof UPDATE_NODE_COMMAND_ID_PREFIX}${string}`;
      context?: UpdateNodeContextType;
    }
  | {
      commandDefinitionId: `${typeof THREAD_COMMAND_ID_PREFIX}${string}`;
      context?: ThreadContextType;
    }
  | { commandDefinitionId: string; context?: CommandContextType };

export type CommandCenterActionsType = {
  pushToCommandStack: (input: PushToCommandStackInputType) => void;
  popCommandsFromStack: (popCount?: number) => void;
  updateCommandContext: (
    commandInstanceId: string,
    context?: CommandContextType,
  ) => void;
  clearCommandStack: () => void;
  registerCommandDefinition: (command: CommandDefinitionType) => void;
  updateCommandDefinition: (
    commandDefinitionId: string,
    command: Partial<CommandDefinitionType>,
  ) => void;
  unregisterCommandDefinition: (commandDefinitionId: string) => void;
  setCommandSearchValue: (commandSearchValue: string) => void;
  setCommandCenterEnterHandler: (handler: (() => void) | null) => void;
};

export type CommandCenterSliceType = CommandCenterStateType &
  CommandCenterActionsType;

export type CommandCenterStateType = {
  commandInstanceStack: {
    commandDefinitionId: string;
    commandInstanceId: string;
    context?: CommandContextType;
  }[];
  commandDefinitions: CommandDefinitionType[];
  commandDefinitionIdOfQuickActions: string;
  commandSearchValue: string;
  commandCenterEnterHandler: (() => void) | null;
  dialogSize: DialogSizeType | null;
  /** Layout mode override for the dialog: "tabs" or "split" (null = auto-detect) */
  dialogLayoutMode: "tabs" | "split" | null;
  /** Whether the dialog is forced to fullscreen (overrides command default) */
  dialogIsFullScreen: boolean;
  /** Number of groups in the current dialog's node layout (set by NodeLayout) */
  dialogGroupCount: number;
  isCommandCenterSearching: boolean;
};

export type CommandDefinitionType = {
  id: string;
  title: string | React.ReactNode;
  description?: string;
  /** Plain text used to filter the command palette when `title` / `getTitle` is not a string. */
  searchText?: string;
  /** Dynamic title resolver — called at render time for commands whose title depends on current state */
  getTitle?: () => string | React.ReactNode;
  getLabel?: (instanceId: string, context?: CommandContextType) => string;
  getIsConfirmationRequiredOnExit?: (instanceId: string) => boolean;
  ReactIcon?: LucideIcon;
  component?: React.ComponentType<{
    commandInstanceId: string;
    commandDefinitionId: string;
    context?: CommandContextType;
  }>;
  header?: React.ComponentType<{
    commandInstanceId: string;
    commandDefinitionId: string;
    context?: CommandContextType;
  }>;
  footer?: React.ComponentType<{
    commandInstanceId: string;
    commandDefinitionId: string;
    context?: CommandContextType;
  }>;
  /** Whether an instance, by its context, has a footer to show: one with nothing in it is not drawn. */
  hasFooter?: (context?: CommandContextType) => boolean;
  context?: React.ComponentType<{
    commandInstanceId: string;
    commandDefinitionId: string;
    context?: CommandContextType;
  }>;
  /** @deprecated Use isConstrained instead */
  isFullscreen?: boolean;
  /** Constrained mode: maxWidth ~1120px, constrained height with padding */
  isConstrained?: boolean;
  /** Full screen mode: takes up entire viewport with no padding */
  isFullScreen?: boolean;
  isHidden?: boolean;
  /** @deprecated Use `sizes` array instead */
  isResizable?: boolean;
  /** Array of dialog sizes to cycle through with the resize button (e.g. ["md", "lg"]) */
  sizes?: DialogSizeType[];
  allowEscape?: boolean;
  onClick?: () => void;
  onExit?: (commandInstanceId: string) => void;
  /** @deprecated Use isConstrained and isFullScreen boolean props instead */
  size?: DialogSizeType;
  group?: string;
  /** Higher values sort the group earlier in the command list. Default 0. */
  groupPriority?: number;
  /** When true, this command's group is listed first in the palette (before global commands). */
  isContextual?: boolean;
  /**
   * Applied as CommandCenter `StickyLayout` `childrenClassName` for this command’s body.
   * Ignored while the command dialog is fullscreen.
   */
  commandPaletteContentClassName?: string;
};

// Discriminated union for editing field context
export type EditingFieldTableContext = {
  type: "table";
  rowIndex: number;
  columnIndex: number;
  contextId: string;
};

export type EditingFieldEntry = {
  context?: EditingFieldTableContext;
};

/**
 * @deprecated Retained only for backwards-compatible URL parsing of older
 * ``?panel=`` query strings. New code must use ``isNodePanelOpen`` /
 * ``isThreadPanelOpen`` instead. See ``nodePanelConfig.ts`` for per-view
 * node-panel eligibility.
 */
export type ActivePanelModeType = "node" | "thread";

export type StyleThemeType = "boxy" | "rounded";

export type ThreadTabActivityType = {
  isStreaming: boolean;
  pendingDeltaCount: number;
};

export type OpenThreadTabType = {
  id: ThreadIdType;
  label?: string;
  /** Transient tab: the next preview replaces it until it is pinned. */
  isPreview?: boolean;
};

export type CoreActionsType = {
  getEntityIdentifier: () => string | null;
  popNodeIdFromQueue: () => void;
  setActiveAppView: (view: AppViewsType) => void;
  /** @deprecated Prefer ``openThreadTab`` / ``selectThreadTab``. Ensures the tab exists. */
  setActiveThreadNodeId: (nodeId: string | null) => void;
  /** Open or focus a thread in the tab strip and optionally show the panel. */
  openThreadTab: (
    threadNodeId: string | null,
    options?: {
      label?: string;
      activate?: boolean;
      openPanel?: boolean;
      /**
       * Open in the preview tab, replacing the current one. A thread that
       * already has a tab keeps it as it is.
       */
      preview?: boolean;
      /** Open with its changes waiting to be confirmed laid out, to put right. */
      review?: boolean;
    },
  ) => void;
  selectThreadTab: (tabId: ThreadIdType) => void;
  closeThreadTab: (tabId: ThreadIdType) => void;
  closeThreadTabs: (tabIds: Iterable<ThreadIdType>) => void;
  reorderOpenThreadTabs: (orderedTabIds: ThreadIdType[]) => void;
  /** Focus the draft tab, opening one if needed. */
  openNewThreadTab: () => void;
  /**
   * Open a new thread with *files* attached to its first message, ready for
   * a word about them - a photo taken or a file picked on a phone, which has
   * no dropping onto the thread.
   */
  openNewThreadWithFiles: (files: File[]) => void;
  /** Replace the draft tab with a persisted thread id after create. */
  promoteNewThreadTab: (threadId: string, label?: string) => void;
  setThreadTabLabel: (tabId: ThreadIdType, label: string) => void;
  /** Keep a preview tab open: the next preview no longer replaces it. */
  pinThreadTab: (tabId: ThreadIdType) => void;
  setThreadTabActivity: (
    tabId: ThreadIdType,
    activity: Partial<ThreadTabActivityType>,
  ) => void;
  /**
   * Open / focus the side panel for ``nodeId``.
   *
   * ``options.queue`` (default ``false``) — when ``true``, push the
   *   currently-active panel id onto ``queuedPanelNodeIds`` so the user can
   *   navigate back via ``popNodeIdFromQueue``.
   * ``options.autofocus`` (default ``true``) — when ``true``, the panel's
   *   ``NodeLayout`` will steal focus to the first field on mount. Workflow
   *   canvas opens (clicking a step / opening an execution drawer) pass
   *   ``false`` so the body field input doesn't pop into edit mode.
   *
   * The id is stored *per active app view* — switching views does not
   * carry the id over. Reading ``state.activePanelNodeId`` returns the
   * mirrored value for the *currently active* view.
   */
  setActivePanelNodeId: (
    nodeId: string | null,
    options?: { queue?: boolean; autofocus?: boolean },
  ) => void;
  setActiveTab: (tab: AppViewTabModeType) => void;
  setActiveTabNodeIds: (tabNodeIds: ActiveTabNodeIdType) => void;
  setDataViewMode: (mode: ViewMode, view?: AppViewsType) => void;
  setIsMutating: (isMutating: "in-flight" | "success" | "error" | null) => void;
  setIsThreadListCollapsed: (isThreadListCollapsed: boolean) => void;
  /**
   * Primitive setter — flips ``isNodePanelOpen``. For the common
   * "open the node panel against this id" pattern, prefer
   * ``openNodePanel(id, options)`` which handles the id, the open flag and
   * autofocus in one shot.
   */
  setIsNodePanelOpen: (isOpen: boolean) => void;
  setIsThreadPanelOpen: (isOpen: boolean) => void;
  /** Convenience: set the panel id and open the Node panel. */
  openNodePanel: (
    nodeId: string | null,
    options?: { queue?: boolean; autofocus?: boolean },
  ) => void;
  /** Convenience: open the Thread panel (Node panel state is untouched). */
  openThreadPanel: (options?: { autofocus?: boolean }) => void;
  /** Closes both panels. */
  closeAllPanels: () => void;
  setIsPanelResizing: (isPanelResizing: boolean) => void;
  setIsSidebarMinimized: (isSidebarMinimized: boolean) => void;
  setNodePanelSize: (nodePanelSize: number) => void;
  setThreadPanelWidth: (threadPanelWidth: number) => void;
  setIsSidebarResizing: (isSidebarResizing: boolean) => void;
  setSnackbarMessage: (message: string) => void;
  setStyleTheme: (styleTheme: StyleThemeType) => void;
  setPanelHeaderExtras: (extras: React.ReactNode | null) => void;
  setActiveNodePanelGroupId: (typename: string, groupId: string) => void;
  setNodeDetailLayoutMode: (typename: string, mode: "tabs" | "split") => void;
  setTriageOneAtATime: (isOneAtATime: boolean) => void;
  setHiddenTriageRowParts: (parts: TriageRowPart[]) => void;
  setTriageMirrored: (isMirrored: boolean) => void;
  setTimeSeriesFilter: (filters: {
    startDate: Date | null;
    endDate: Date | null;
    frequency: FrequencyEnum | null;
  }) => void;
};

export type CoreSliceType = CoreStateType & CoreActionsType;

export type CoreStateType = {
  activeAppView: AppViewsType;
  activeThreadNodeId: string | null;
  /** Chrome-style open threads in the global thread panel. */
  openThreadTabs: OpenThreadTabType[];
  /** Per-tab streaming / pending-delta indicators for the tab strip. */
  threadTabActivity: Partial<Record<ThreadIdType, ThreadTabActivityType>>;
  activeEntityIdentifiers: string[];
  /**
   * Per-view active node id for the Node panel. Keyed on ``AppViewsType``;
   * switching views does *not* carry an id over (the Node panel is
   * contextual to a particular view — see ``nodePanelConfig.ts``).
   */
  activePanelNodeIds: Partial<Record<AppViewsType, string | null>>;
  /**
   * Mirror of ``activePanelNodeIds[activeAppView]`` — denormalised so that
   * the many call sites that read a single "current" id continue to work
   * with no per-view plumbing. Always written by the slice; never set
   * directly from components.
   */
  activePanelNodeId: string | null;
  /** When the side panel opens, should ``NodeLayout`` autofocus its first field? */
  activePanelAutofocus: boolean;
  /**
   * Optional slot for view-specific affordances rendered into the panel
   * header next to the title (passed as ``NodeTitleLayout.endDecorator``).
   * Views push their own affordances (e.g. the workflow editor's
   * "Convert to Switch" toggle) and clear them on unmount. Keeps
   * ``NodePanelLayout`` object-agnostic.
   */
  panelHeaderExtras: React.ReactNode | null;
  /** Last-selected ``NodeLayout`` group tab per object typename. */
  activeNodePanelGroupIds: Record<string, string>;
  /**
   * Detail-page layout preference per object typename. "split" is only
   * honoured when the page is wide enough for the columns — see
   * ``useResponsiveLayoutMode`` — so this is a preference, not an override.
   */
  nodeDetailLayoutModes: Record<string, "tabs" | "split">;
  /**
   * Triage's queue and the payment open one at a time - a payment clicked
   * into, and back - rather than side by side: the person's choice, beside
   * the queue's filters. A phone, or a window too narrow for both, has them
   * one at a time whatever is chosen.
   */
  isTriageOneAtATime: boolean;
  /** What triage's rows leave out: the person's choice, under the queue's eye. */
  hiddenTriageRowParts: TriageRowPart[];
  /**
   * What triage lines up beside the payments drawn mirrored - amount and date
   * first, what it is last - so the two sides face each other: the person's
   * choice, on the suggestions' toolbar.
   */
  isTriageMirrored: boolean;
  activeTab: AppViewTabModeType;
  activeTabNodeIds: ActiveTabNodeIdType;
  dataViewModes: Record<string, ViewMode>;
  entities: EntityFragment[];
  isThreadListCollapsed: boolean;
  isMutating: "in-flight" | "success" | "error" | null;
  /** Is the Node panel (contextual, view-specific, sits below Topbar 2) open? */
  isNodePanelOpen: boolean;
  /** Is the Thread panel (global, full-height, far right) open? */
  isThreadPanelOpen: boolean;
  /**
   * Incremented by ``openThreadPanel({ autofocus: true })`` so the thread
   * composer can re-focus even when the panel is already open.
   */
  threadPanelFocusRequestId: number;
  /**
   * Files for the thread panel to attach to its draft as it opens
   * (``openNewThreadWithFiles``): it takes them, and clears this. Never kept
   * across a reload.
   */
  threadPanelPendingFiles: File[];
  /**
   * A thread asked to open with its changes waiting to be confirmed laid out
   * (``openThreadTab({ review: true })``): it lays them out, and clears this.
   */
  threadReviewRequestNodeId: string | null;
  isPanelResizing: boolean;
  isSidebarMinimized: boolean;
  /**
   * How much of the room beside the view the node panel takes, as a
   * percentage, from the last time it was resized; ``null`` until it has been.
   * Kept across reloads, so the panel opens at the size it was left at.
   */
  nodePanelSize: number | null;
  /**
   * The chat panel's width in pixels; ``null`` until it has been open. Set by
   * the chat panel while open beside a page, and by the thread view's list,
   * which is four-fifths of it: the list opens at that share, and the chat
   * reopens at the width the list implies.
   */
  threadPanelWidth: number | null;
  isSidebarResizing: boolean;
  /** The backend has refused this tab's queries as written against a schema it no longer has. */
  isClientOutdated: boolean;
  onSetActiveTab: ((tab: AppViewTabModeType) => void) | null;
  previousAppView: AppViewsType | null;
  queuedPanelNodeIds: string[];
  reviewableDialogNodeIds: string[];
  scopedEntityIdentifier: string | null;
  /**
   * The grants a client's books are opened through - an accountant working in
   * a client's books, one grant a business of theirs - or none, in their own.
   * Sent with every request (``X-External-Grant``, comma-separated) and on the
   * socket, so the server reads those businesses alone.
   */
  externalGrantIdentifiers: string[];
  snackbarMessage: string;
  styleTheme: StyleThemeType;
  timeSeriesFilter: {
    endDate: Date | null;
    frequency: FrequencyEnum | null;
    startDate: Date | null;
  };
  userIdentifier: string | null;
};

// Dashboard layout profiles
export type BreakpointKey = "xs" | "sm" | "md" | "lg" | "xl";

export type DashboardCardLayout = {
  cardId: string;
  colSpan: number;
  visible: boolean;
};

export type BreakpointLayout = {
  cards: DashboardCardLayout[];
};

/** A report's layouts under a name - what a report layout record maps to. */
export type DashboardProfile = {
  id: string;
  name: string;
  layouts: Record<BreakpointKey, BreakpointLayout>;
};

export type DashboardStateType = {
  currentDashboardBreakpoint: BreakpointKey;
  /** Active report tab id per report layout identifier. */
  activeReportTabIds: Record<string, string>;
};

export type DashboardActionsType = {
  setCurrentDashboardBreakpoint: (breakpoint: BreakpointKey) => void;
  setActiveReportTabId: (layoutIdentifier: string, tabId: string) => void;
};

export type DashboardSliceType = DashboardStateType & DashboardActionsType;

export type GlobalStoreStateType = ThreadSliceType &
  CommandCenterSliceType &
  CoreSliceType &
  NavigationSliceType &
  UtilityBarSliceType &
  DashboardSliceType;

// Navigation slice - for managing focus origin across remote UI components
export type FocusOriginType = {
  contextNode: unknown; // ContextNode from useContextTree
  elementRef: HTMLElement | null;
};

export type NavigationSliceType = NavigationStateType & NavigationActionsType;

export type NavigationStateType = {
  focusOriginStack: FocusOriginType[];
  /**
   * The list-table cell a routed detail page was opened from. The breadcrumb
   * pager moves it along the list as it steps; the next table to load focuses
   * it (if it holds that row) and clears it either way.
   */
  detailOriginCell: { rowId: string; colIndex: number } | null;
};

export type NavigationActionsType = {
  pushFocusOrigin: (origin: FocusOriginType) => void;
  popFocusOrigin: () => void;
  clearFocusOriginStack: () => void;
};

export type UtilityBarActionsType = {
  setAvailableOperators: (operators: string[][]) => void;
  setFilterDefinitions: (definitions: {
    [key: string]: FilterDefinitionType;
  }) => void;
  setFilters: (filters: FilterType[]) => void;
  setRefetch: (refetch: (sortObj: any, filterObj: any) => void) => void;
  setSortableDefinitions: (
    definitions: Record<string, SortableDefinitionsType>,
  ) => void;
  setSortables: (sortables: SortableType[]) => void;
};

export type UtilityBarSliceType = UtilityBarStateType & UtilityBarActionsType;

export type UtilityBarStateType = {
  availableOperators: string[][];
  filterDefinitions: { [key: string]: FilterDefinitionType };
  filters: FilterType[];
  /**
   * The key of the open list's filter chip asked to open from outside the bar
   * (a column header's "Filter"). That chip opens its picker and clears it.
   */
  openFilterKey: string | null;
  refetch: null | ((sortObj: any, filterObj: any) => void);
  sortableDefinitions: Record<string, SortableDefinitionsType>;
  sortables: SortableType[];
};
